import styles from "./Plan.module.css";
import { RecurlyPlan } from "@/lib/recurly";
import IconRocket from "@/components/icons/IconRocket";
import Checklist from "@/components/Checklist/Checklist";
import Flex from "@/components/Flex/Flex";
import Divider from "@/components/Divider/Divider";


const STANDARD_PLAN_FEATURES = [
  { label: "Optimized Shipment Flow", checked: true },
  { label: "Inventory Management", checked: true },
  { label: "Shipments Manager", checked: true },
  { label: "Sales Analytics", checked: true }
]

const PROFESSIONAL_PLAN_FEATURES = [
  { label: "Everything in Standard Plan PLUS", checked: true },
  { label: "LTL Shipments", checked: true },
  { label: "2D Barcodes", checked: true },
  { label: "Early access to Beta features", checked: true }
]

function Plan({ plan, prorate }: { plan: RecurlyPlan, prorate?: number }) {
  if (!plan || !(plan?.code)) {
    return <div>No plan data available</div>;
  }
  
  const intervalFrequency = plan.interval_length === 1 ? "monthly" : "annually";
  const intervalUnits = plan.interval_length === 1 ? "month" : "months";
  const totalCost = plan.currencies[0].unit_amount;
  let intervalCost = (plan.currencies[0].unit_amount / plan.interval_length).toFixed(2);

  if (intervalCost.endsWith(".00")) {
    intervalCost = intervalCost.substring(0, intervalCost.length - 3)
  }

  // determine the first billing date after the free trial ends
  const firstBillingDate = new Date();
  
  if (plan.trial_unit === "months") {
    firstBillingDate.setMonth(firstBillingDate.getMonth() + plan.trial_length);
  } else {
    // calculate trial length in days
    firstBillingDate.setDate(firstBillingDate.getDate() + plan.trial_length);
  }

  let totalDueToday = plan.trial_length > 0 ? 0.00 : totalCost;
  const setupFee = plan.currencies[0]?.setup_fee;
  if (setupFee) {
    totalDueToday += setupFee;
  }

  const isProfessional997WEbinarDeal = plan.code === "professional-997-webinaroffer";

  return (
    <Flex className={styles.root} gap={2} direction="column" justify="space-between">
      <Flex direction="column" gap={4}>
        <Flex className={`weak`} justify="center">
          Your Subscription Summary
        </Flex>
        <Flex gap={2} justify="space-between">
          <Flex direction="column" gap={1}>
            <div
              className={`strong`}
            >
              {plan.name === "professional-wholesale-webinar" ? "Professional Wholesale Webinar" : plan.name}
            </div>
            <div className={`weak}`}>Billed {intervalFrequency}</div>
          </Flex>
          {!isProfessional997WEbinarDeal && (
            <Flex direction="column" gap={1}>
              <div className={`strong`}>
                ${intervalCost}
              </div>
              <div className={`weak`}>
                x {plan.interval_length} {intervalUnits}
              </div>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Divider/>
      <Checklist
        color="primary"
        items={plan.code.startsWith("professional") ? PROFESSIONAL_PLAN_FEATURES : STANDARD_PLAN_FEATURES}
      />
      <Divider/>
      {isProfessional997WEbinarDeal && (
        <>
          <div className={styles.roadmapBadgeContainer}>
            <IconRocket size={16} />
            FBA ROADMAP INCLUDED
          </div>
          <Divider/>
        </>
      )}
      {plan.code === "professional-wholesale-webinar" && (
        <>
          <div className={styles.roadmapBadgeContainer}>
            <IconRocket size={16} />
            WHOLESALE ROADMAP INCLUDED
          </div>
          <Divider/>
        </>
      )}
      {(!isProfessional997WEbinarDeal && plan.code !== "professional-wholesale-webinar") && (
        <>
          <Flex
            className={`strong`}
            justify="space-between"
          >
            <div>
              {plan.name} - {plan.interval_length} {intervalUnits}
            </div>
            <div>${totalCost}</div>
          </Flex>
          <Divider/>
        </>
      )}
      <Flex direction="column" gap={4}>
        <Flex justify="space-between">
          <div className={`strong`}>Total due today:</div>
          <div>
          {isProfessional997WEbinarDeal ? (
            <>
              $797.00
            </>
          ) : (
            <>
              ${prorate ? prorate.toFixed(2).toString() : totalDueToday.toFixed(2)}
            </>
          )}
        </div>
        </Flex>
        <div className={`weak`}>
          {plan.trial_length > 0 && (
            <>
              {isProfessional997WEbinarDeal ? (
                <>
                  1-year free trial, then
                  <br/>
                </>
              ) : (
                <>
                  {plan.trial_length}-day free trial, then
                  <br/>
                </>
              )}
            </>
          )}
          Billed {intervalFrequency} @ ${totalCost}
          <br />
          on {firstBillingDate.toLocaleDateString("en-us", { year: "numeric", month: "long", day: "numeric" })}
        </div>
      </Flex>
      <div className={`weak`}>
        This prorated amount accounts for any coupons applied to first-time purchases only. Amount above may vary for
        users with existing subscriptions due to plan change proration.
      </div>
    </Flex>
  );
}

export default Plan;
